import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/NewButton";
import { Input } from "@/components/ui/input";
import { Tag, useTags } from '@/hooks/useTags';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useParams } from '@tanstack/react-router';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { useMatterLabels } from '@/lib/useMatterLabels';
import { useDocuments } from '@/hooks/useDocuments';
import { useTagEditor } from '@/context/TagEditorContext';
import { SimpleColorPickerGrid } from './SimpleColorPickerGrid';
import Loading from './Loading';

// Create a context for sharing colors with child components
const TagEditorContext = React.createContext<{
  predefinedColors: string[]
}>({
  predefinedColors: []
});

interface TagRowProps {
  tag: Tag;
  isNew?: boolean;
  isHighlighted?: boolean;
  onChange: (guid: string, field: keyof Tag, value: string) => void;
  onDelete?: (guid: string, tagName: string) => void;
  activeColorPicker: string | null;
  setActiveColorPicker: (id: string | null) => void;
}

const TagRow: React.FC<TagRowProps> = ({
  tag,
  isNew = false,
  isHighlighted = false,
  onChange,
  onDelete,
  activeColorPicker,
  setActiveColorPicker
}) => {
  // Create a sample tag swatch for visual preview
  const tagSwatch = (
    <div
      className="h-5 w-full rounded-md"
      style={{ backgroundColor: tag.color || '#CCCCCC' }}
    />
  );

  // Create highlight animation effect if this is the latest added tag
  const highlightClass = isHighlighted ? 'animate-highlight-fade' : '';

  // Get predefined colors from the parent component
  const { predefinedColors } = React.useContext(TagEditorContext);

  // Handle color picker open state
  const isColorPickerOpen = activeColorPicker === tag.guid;
  const handleColorPickerOpenChange = (isOpen: boolean) => {
    setActiveColorPicker(isOpen ? tag.guid : null);
  };

  return (
    <tr className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${isNew ? 'bg-blue-50' : ''} ${highlightClass}`}>
      <td className="p-2 pl-3">
        <Input
          className={`w-full px-2.5 py-1.5 focus:ring-2 focus:ring-blue-500 text-sm font-medium ${!tag.name?.trim() ? 'border-red-300' : 'border-input'}`}
          value={tag.name}
          onChange={(e) => onChange(tag.guid, 'name', e.target.value)}
          placeholder="Enter tag name"
          aria-label="Tag name"
          maxLength={30}
          required
        />
      </td>
      <td className="p-2 hidden sm:table-cell">
        <div className="relative">
          <textarea
            className={`w-full px-2.5 py-1.5 rounded-md border ${!tag.description?.trim() ? 'border-red-300' : 'border-input'} text-sm focus:ring-2 focus:ring-blue-500 min-h-[76px] resize-none pr-16 bg-white`}
            value={tag.description || ''}
            onChange={(e) => onChange(tag.guid, 'description', e.target.value)}
            placeholder="Enter description (required)"
            aria-label="Tag description"
            maxLength={300}
            required
          />
          <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
            {(tag.description?.length || 0)}/300
          </div>
        </div>
      </td>
      <td className="p-2 relative">
        <div className="flex justify-center">
          <SimpleColorPickerGrid
            selectedColor={tag.color || '#CCCCCC'}
            onColorSelect={(color) => onChange(tag.guid, 'color', color)}
            colors={predefinedColors}
            isOpen={isColorPickerOpen}
            onOpenChange={handleColorPickerOpenChange}
            subtle
          />
        </div>
      </td>
      {onDelete && (
        <td className="p-2 w-10">
          <div className="flex justify-center">
            <Button
              variant="ghost"
              className="text-gray-500 hover:text-red-500 hover:bg-red-50"
              onClick={() => onDelete(tag.guid, tag.name)}
              title="Delete tag"
              aria-label={`Delete tag ${tag.name}`}
              icon={Trash2}
            />
          </div>
        </td>
      )}
    </tr>
  );
};

export const TagEditorDialog: React.FC = () => {
  const { slug } = useParams({ from: '/matter/$slug' });
  const label = useMatterLabels();
  const { tags, isLoading, mutateTags } = useTags();
  const [editedTags, setEditedTags] = useState<Tag[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<{ guid: string, name: string } | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [lastAddedTagId, setLastAddedTagId] = useState<string | null>(null);
  const [activeColorPicker, setActiveColorPicker] = useState<string | null>(null);
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [predefinedColors, setPredefinedColors] = useState<string[]>([
    '#E0E0E0', '#BDBDBD', '#90A4AE', '#A5D6A7', '#FFE082', '#FFAB91',
    '#FFADAD', '#FFD6A5', '#FDFFB6', '#CAFFBF', '#9BF6FF',
    '#A0C4FF', '#BDB2FF', '#FFC6FF', '#FFB5E8', '#E0BBE4',
    '#C7CEEA', '#FFDFD3', '#FEC8D8', '#D5A6BD', '#C6E2E9',
    '#F0EAD6', '#D4A5A5', '#D6E2E9', '#D1CFE2', '#FFB7C5',
    '#F5C7F7', '#D9EAD3', '#FFEBB7', '#D3E2A5', '#A7BED3',
    '#FFF5BA', '#E6E6FA', '#C1D3FE', '#DBD3FF', '#F0F4C3',
    '#B0D0BC', '#C9BBCF', '#FFDAC1', '#BFD8B8', '#D8A7B1',
    '#CCE2CB', '#B4D2BA', '#F2C6DE', '#B5BAD0', '#D3C0EB',
    '#E3D4B9', '#B5EAD7', '#C2B9F0', '#F6D6AD', '#EAD2AC',
    '#B9E3C6', '#FADADD', '#ADC2EB', '#DACDBA', '#CAD2C5',
    '#E1C6AC', '#D6CDEA', '#F2E2BA', '#B9D9EB', '#FFE1A8',
    '#C8D5B9', '#B2A4FF', '#FAD3CF', '#C4DFAA', '#CFC1FF'
  ]);

  // Use the global tag editor state
  const { isTagEditorOpen, closeTagEditor } = useTagEditor();

  // Close any open color picker when clicking on the dialog content
  const handleDialogClick = () => {
    if (activeColorPicker) {
      setActiveColorPicker(null);
    }
  };

  // Get documents data for refreshing after tag deletion
  const { mutate: mutateDocuments } = useDocuments(
    slug,
    ["correspondence"]
  );

  // Generate a temporary frontend ID for new tags
  const generateTempId = () => `temp-${Math.random().toString(36).substr(2, 9)}`;

  // Update editedTags when tags are loaded
  useEffect(() => {
    if (tags && tags.length > 0) {
      // Sort tags alphabetically when loading from backend
      setEditedTags(tags.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    }
  }, [tags]);

  const handleChange = (guid: string, field: keyof Tag, value: string) => {
    setEditedTags((prevTags) =>
      prevTags.map((tag) =>
        tag.guid === guid ? { ...tag, [field]: value } : tag
      )
    );
  };

  const handleAddNewTag = () => {
    // Check if there's already an empty tag
    const hasEmptyTag = editedTags.some(tag =>
      (!tag.name || tag.name.trim() === '') ||
      (!tag.description || tag.description.trim() === '')
    );

    if (hasEmptyTag) {
      toast.error("Please complete the existing tags before adding a new one");
      return;
    }

    const newTagId = generateTempId();
    // Get a random color from the predefined colors palette
    const randomColor = predefinedColors[Math.floor(Math.random() * predefinedColors.length)];
    const newTag: Tag = {
      guid: newTagId,
      name: '',
      description: '',
      color: randomColor, // Random color from palette instead of default gray
      created_at: new Date().toISOString()
    };
    setEditedTags([newTag, ...editedTags]);
    setLastAddedTagId(newTagId);
  };

  // Scroll to top when a new tag is added
  useEffect(() => {
    if (lastAddedTagId && tableContainerRef.current) {
      setTimeout(() => {
        if (tableContainerRef.current && tableContainerRef.current.scrollTop > 0) {
          tableContainerRef.current.scrollTop = 0;
        }
      }, 10);
    }
  }, [lastAddedTagId]);

  const handleDeleteTagClick = (guid: string, tagName: string) => {
    // Only show confirmation for existing tags (not temporary ones)
    if (!guid.startsWith('temp-')) {
      setTagToDelete({ guid, name: tagName });
      setShowConfirmDelete(true);
    } else {
      // For temporary tags, just remove from the list without confirmation
      setEditedTags((prevTags) => prevTags.filter(tag => tag.guid !== guid));
    }
  };

  const handleConfirmTagDelete = async () => {
    if (!slug || !tagToDelete) return;

    setIsDeleting(true);

    try {
      console.log(`Deleting tag: ${tagToDelete.name} (${tagToDelete.guid})`);

      // Call the tag deletion API endpoint
      const response = await backendClient.delete(`/tags/${slug}/delete`, {
        data: { tag_guid: tagToDelete.guid }
      });

      console.log('Tag deletion response:', response.data);

      // Remove the deleted tag from the local state
      setEditedTags((prevTags) => prevTags.filter(tag => tag.guid !== tagToDelete.guid));

      // Close the confirmation dialog
      setShowConfirmDelete(false);
      setTagToDelete(null);

      // Refresh both tag data and document data
      await mutateTags();
      await mutateDocuments();

      toast.success(`Tag '${tagToDelete.name}' deleted successfully`);
    } catch (error: unknown) {
      console.error('Error deleting tag:', error);
      toast.error(`Failed to delete tag: ${apiErrorMessage(error) || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteTag = (guid: string) => {
    setEditedTags((prevTags) => prevTags.filter(tag => tag.guid !== guid));
  };

  const handleSave = async () => {
    if (!slug) return;

    // Check if any tags are missing names
    const tagsWithoutName = editedTags.filter(
      tag => !tag.name || tag.name.trim() === ''
    );

    if (tagsWithoutName.length > 0) {
      // Show error message
      toast.error(`${tagsWithoutName.length} tag(s) are missing required names`);
      return;
    }

    // Check if any tags are missing descriptions
    const tagsWithoutDescription = editedTags.filter(
      tag => (!tag.description || tag.description.trim() === '')
    );

    if (tagsWithoutDescription.length > 0) {
      // Show error message
      toast.error(`${tagsWithoutDescription.length} tag(s) are missing required descriptions`);
      return;
    }

    setIsSaving(true);

    try {
      // All tags are valid at this point (we've already checked for empty names and descriptions)
      const validTags = editedTags;

      // Split into existing tags and new tags
      const existingTags = validTags.filter(tag => !tag.guid.startsWith('temp-'));
      const newTags = validTags.filter(tag => tag.guid.startsWith('temp-')).map(tag => ({
        name: tag.name,
        description: tag.description,
        color: tag.color
      }));

      // Prepare the payload
      const payload = {
        updatedTags: existingTags,
        newTags: newTags
      };

      // Save tags
      await backendClient.post(`/tags/${slug}/update`, payload);

      // Refresh tag data
      mutateTags();

      // Refresh document list to show updated tags
      await mutateDocuments();

      // Close dialog
      closeTagEditor();
      toast.success('Tags updated successfully');
    } catch (error: unknown) {
      console.error('Error saving tags:', error);
      toast.error(apiErrorMessage(error) || 'Failed to update tags');
    } finally {
      setIsSaving(false);
    }
  };

  // Update editedTags when dialog opens
  useEffect(() => {
    if (isTagEditorOpen && tags && tags.length > 0) {
      // Sort tags alphabetically when dialog opens
      setEditedTags(tags.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })));
    } else if (isTagEditorOpen) {
      setEditedTags([]);
    }
  }, [isTagEditorOpen, tags]);

  return (
    <>
      <TagEditorContext.Provider value={{
        predefinedColors
      }}>
      <Dialog
        open={isTagEditorOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeTagEditor();
          }
        }}
      >
        <DialogContent
          className="w-[95vw] sm:max-w-[800px] md:max-w-[900px] lg:max-w-[1000px] max-h-[85vh] min-h-[360px] flex flex-col"
        >
          <DialogHeader className="shrink-0 pb-1">
            <DialogTitle className="text-lg">Manage Tags</DialogTitle>
            <DialogDescription className="mt-1">
              {label('tag-editor-organise-hint')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 pt-0 pb-0 flex flex-col overflow-hidden" onClick={handleDialogClick}>
            {isLoading ? (
              <div className="text-center py-8 text-gray-600">
                <Loading text='Loading tags' inline />
              </div>
            ) : (
              <>
                <div className="flex justify-end shrink-0 mb-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddNewTag}
                    text='Add tag'
                    icon={Plus}
                  />
                </div>
                <div className="rounded-md border border-gray-200 shadow-md flex-1 flex flex-col min-h-0 m-0 p-0">
                  <div className="overflow-auto flex-1 m-0 p-0" ref={tableContainerRef}>
                    <table className="w-full border-collapse m-0 p-0">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
                          <th className="p-2 pl-3 w-[25%] md:w-[30%] border-b border-gray-200">
                            Name <span className="text-red-500">*</span>
                          </th>
                          <th className="p-2 w-[45%] md:w-[50%] border-b border-gray-200 hidden sm:table-cell">
                            Description <span className="text-red-500">*</span>
                          </th>
                          <th className="p-2 w-[15%] md:w-[10%] border-b border-gray-200 text-center">{label('colour')}</th>
                          <th className="p-2 w-[5%] border-b border-gray-200"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editedTags.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center py-12 text-gray-500">
                              <div className="flex flex-col items-center gap-3">
                                <div className="rounded-full bg-gray-100 p-3 inline-block">
                                  <Plus className="h-6 w-6 text-gray-400" />
                                </div>
                                <span>No tags found. Click "Add Tag" to create one.</span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          editedTags.map((tag) => (
                            <TagRow
                              key={tag.guid}
                              tag={tag}
                              isNew={tag.guid.startsWith('temp-')}
                              isHighlighted={tag.guid === lastAddedTagId}
                              onChange={handleChange}
                              onDelete={handleDeleteTagClick}
                              activeColorPicker={activeColorPicker}
                              setActiveColorPicker={setActiveColorPicker}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="pt-2 pb-1 shrink-0">
            <div className="flex items-center justify-end w-full">
              <Button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                text='Save changes'
                loading={isSaving}
                loadingText='Saving'
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TagEditorContext.Provider>

      {/* Confirmation dialog for tag deletion */}
      {tagToDelete && (
        <Dialog open={showConfirmDelete} onOpenChange={setShowConfirmDelete}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 text-xl">
                <AlertCircle className="h-6 w-6" />
                Confirm Tag Deletion
              </DialogTitle>
              <DialogDescription className="mt-3 text-base">
                Are you sure you want to delete the tag <span className="font-medium">"{tagToDelete.name}"</span>?
                This action cannot be undone.
              </DialogDescription>
              <DialogDescription className="mt-4 p-3 bg-red-50 border border-red-100 rounded-md text-red-700 font-medium">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                  <span>This will remove the tag from all documents where it has been assigned.</span>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirmDelete(false);
                    setTagToDelete(null);
                  }}
                  disabled={isDeleting}
                  text='Cancel'
                />
                <Button
                  variant="destructive"
                  onClick={handleConfirmTagDelete}
                  loading={isDeleting}
                  loadingText='Deleting'
                  text='Delete Tag'
                />
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
